import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { TreasuryService } from '../../services/treasury.service';
import { PERMISSIONS } from '../../constants/permissions';
import { SuggestedAccountsResult, TreasurySettings as Settings } from '../../interfaces/treasury.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { segmentLabel, useTreasuryRefData } from './treasuryRef';

type AccountKey = 'cashInTransitAccountId' | 'cashOverShortAccountId' | 'defaultFdrAccountId'
  | 'defaultInterestReceivableAccountId' | 'defaultInterestIncomeAccountId' | 'defaultAitAccountId' | 'defaultChargesAccountId';

const FIELDS: { key: AccountKey; label: string; pool: 'assets' | 'expenses' | 'revenues' | 'all' }[] = [
  { key: 'cashInTransitAccountId', label: 'Cash in transit account', pool: 'assets' },
  { key: 'cashOverShortAccountId', label: 'Cash over/short account', pool: 'all' },
  { key: 'defaultFdrAccountId', label: 'Default FDR principal account', pool: 'assets' },
  { key: 'defaultInterestReceivableAccountId', label: 'Default interest receivable account', pool: 'assets' },
  { key: 'defaultInterestIncomeAccountId', label: 'Default interest income account', pool: 'revenues' },
  { key: 'defaultAitAccountId', label: 'Default AIT account', pool: 'assets' },
  { key: 'defaultChargesAccountId', label: 'Default charges account', pool: 'expenses' },
];

/** Help attributes of each account field (literals so the help key scan sees them). */
const FIELD_HELP: Record<AccountKey, Record<string, string>> = {
  cashInTransitAccountId: { 'data-help': 'bank.treasurySettings.field.cashInTransit' },
  cashOverShortAccountId: { 'data-help': 'bank.treasurySettings.field.cashOverShort' },
  defaultFdrAccountId: { 'data-help': 'bank.treasurySettings.field.fdrAccount' },
  defaultInterestReceivableAccountId: { 'data-help': 'bank.treasurySettings.field.interestReceivable' },
  defaultInterestIncomeAccountId: { 'data-help': 'bank.treasurySettings.field.interestIncome' },
  defaultAitAccountId: { 'data-help': 'bank.treasurySettings.field.aitAccount' },
  defaultChargesAccountId: { 'data-help': 'bank.treasurySettings.field.chargesAccount' },
};

const TreasurySettings: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const ref = useTreasuryRefData(e => toast.showApiError(e, 'Failed to load reference data'));
  const [settings, setSettings] = useState<Settings>({ institutionId });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SuggestedAccountsResult | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setSettings(await TreasuryService.getSettings(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load treasury settings');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const set = (k: keyof Settings, v: string) => setSettings(s => ({ ...s, [k]: v || null }));

  const save = async () => {
    try {
      setSaving(true);
      setSettings(await TreasuryService.saveSettings({ ...settings, institutionId }));
      toast.showSuccess('Settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const suggest = async () => {
    try {
      setSaving(true);
      const r = await TreasuryService.createSuggestedAccounts(institutionId);
      setResult(r);
      setSettings(r.settings);
      toast.showSuccess('Suggested accounts created');
    } catch (e) {
      toast.showApiError(e, 'Failed to create suggested accounts');
    } finally {
      setSaving(false);
    }
  };

  const poolOf = (p: string): Account[] =>
    p === 'assets' ? ref.assets : p === 'expenses' ? ref.expenses : p === 'revenues' ? ref.revenues
      : [...ref.expenses, ...ref.revenues, ...ref.assets];

  return (
    <Box data-help-page="bank.treasurySettings.page" sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Treasury settings</Typography>
      {loading ? <CircularProgress /> : (
        <Box data-help-section="bank.treasurySettings.section.accounts" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            Accounts used by transfers, FDRs and petty-cash counts. "Create suggested accounts" adds Cash in Transit and Interest
            Receivable under the standard parents and links them here. Nothing is created until you click it.
          </Alert>
          {canManage && <Box><Button data-help="bank.treasurySettings.suggest" variant="outlined" disabled={saving} onClick={suggest}>Create suggested accounts</Button></Box>}
          {result && (
            <Alert severity="success" data-testid="suggest-result">
              {result.created.length > 0 && <div>Created: {result.created.join(', ')}</div>}
              {result.present.length > 0 && <div>Already present: {result.present.join(', ')}</div>}
              {result.skipped.length > 0 && <div>Skipped: {result.skipped.join(', ')}</div>}
            </Alert>
          )}
          {FIELDS.map(f => (
            <AccountAutocomplete {...FIELD_HELP[f.key]} key={f.key} accounts={poolOf(f.pool)} value={settings[f.key] ?? ''} label={f.label}
              disabled={!canManage} onChange={id => set(f.key, id)} />
          ))}
          <SearchableSelect<Segment> data-help="bank.treasurySettings.field.segment" label="Default segment" options={ref.segments} value={settings.defaultSegmentId ?? ''}
            getOptionLabel={segmentLabel} disabled={!canManage} onChange={id => set('defaultSegmentId', id)} />
          {canManage && <Box><Button data-help="bank.treasurySettings.save" variant="contained" disabled={saving} onClick={save}>Save settings</Button></Box>}
        </Box>
      )}
    </Box>
  );
};

export default TreasurySettings;
